import type { AgentFocus } from '@tunasong/agent-context'
import { useAudioEngine } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { shortUuid } from '@tunasong/models'
import { AssistantAvatarContext, Chat } from '@tunasong/plugin-chat/ui'
import type { ChatProps, ComposerSeed } from '@tunasong/plugin-chat/ui'
import { useEditor, usePlugins } from '@tunasong/plugin-lib'
import { features, nlpApi, startAgentTurn, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Agent, ColorName, Comment, Persisted } from '@tunasong/schemas'
import { Avatar, getColorCSS } from '@tunasong/ui-lib'
import type { CSSProperties, FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Node } from 'slate'
import styles from './agent-chat.module.css'
import { resolveAgentVisuals } from './agent-visuals.js'
import { hasPersistedReply } from './find-persisted-reply.js'
import { LiveAgentResponse } from './live-agent-response.js'
import { useClientToolHost } from './use-client-tool-host.js'
import { useAgentTurnStream } from './use-agent-turn-stream.js'
import { useComposerIntent } from './use-composer-intent.js'

/**
 * Same shape as ChatProps but with `element` narrowed to Agent — agentStream
 * only makes sense for agent entities, so we lock the type at the seam rather
 * than runtime-guard inside the handler.
 *
 * `composerId` passes through to the underlying ChatBox so callers (the
 * sidebar empty-state header) can wire aria-describedby without reaching
 * into Chat's internals.
 */
export interface AgentChatProps extends Omit<ChatProps, 'element'> {
  element: Persisted<Agent> | Agent
}

/**
 * Read the text body of a chat comment by walking its Slate tree.
 * Returns '' for empty or whitespace-only comments so callers can short-circuit.
 */
const commentText = (comment: Comment): string =>
  (comment.children ?? [])
    .map(child => Node.string(child as never))
    .join('\n')
    .trim()

/**
 * Wraps Chat with agent-specific behavior: when the LOCAL user posts a comment
 * via the ChatBox, fire nlpApi.agentStream so runAgent runs server-side. The
 * response streams live below the persisted-comment feed via
 * `useAgentTurnStream` + `LiveAgentResponse`, and reconciles against the
 * durable comment by `metadata.requestId` once runAgent persists it: the live
 * item and the pushed comment both derive from store state, so the swap from
 * "streaming" to "persisted" happens within a single render pass — no flicker
 * window (see `hasPersistedReply`/`find-persisted-reply.ts`).
 *
 * Skipping conditions:
 * - `userId === 'assistant'` — the comment is the agent's own response; firing
 *   would create an invocation loop.
 * - empty text — submit-on-enter and similar UX bugs can produce empty
 *   comments; we don't spend an LLM round-trip on them.
 */
export const AgentChat: FC<AgentChatProps> = props => {
  const { element: agent } = props
  const dispatch = useThunkDispatch()

  // Audio engine context — may not be present (audio plugin not mounted on
  // this view). Pass allowNull=true so we get null instead of a throw.
  const audioEngine = useAudioEngine(true)

  // Editor + plugins for client-tool dispatch. AgentChat renders inside the
  // sidebar, which `EntityChrome` mounts *within* its `TunaEditorContext`
  // provider (entity-chrome.tsx wraps <Sidebar> in the provider and feeds it
  // the live editor via onEditor). So the editor in view IS reachable here.
  // allowNull: the agent can also surface outside an editor (returns null →
  // apply_editor_command degrades to `no_editor` gracefully). The context
  // value is the stable editor *instance* reference — Slate mutates
  // editor.selection in place, so reading it here does not re-render on every
  // caret move; only a document switch changes the reference.
  // PluginContext is app-root (di-container), above the sidebar, so usePlugins
  // resolves anywhere in the tree.
  const editor = useEditor({ allowNull: true })
  const plugins = usePlugins('all') ?? []

  // Register a client-tool dispatcher for this agent. editor + plugins are
  // captured fresh each render and read at dispatch time via useClientToolHost's
  // getContextRef, so a document switch propagates to the next tool call.
  // Invariant: the agent is attached to the viewed entity, so `editor` roots
  // the agent's parent (mirrors the editor.rootElement.id === targetEntityId
  // check in proposal-apply.ts). apply_editor_command stays allowlisted to
  // non-destructive UI commands regardless.
  // `agent.id` is `string | undefined` on the bare Agent union; AgentChat only
  // ever renders for a persisted agent, so it is present at runtime. Coalesce
  // to '' to satisfy the `string` param — an empty agentId registers a
  // dispatcher that the agentStream handler never looks up (real streams carry
  // a real id), so it is inert.
  const { resetCounter } = useClientToolHost(agent.id ?? '', () => ({
    editor,
    plugins,
    audioEngine,
  }))

  // Subscribe to the in-flight `agentStream` cache entry for this agent so we
  // can render the live response below the persisted-comment feed. The hook
  // returns `null` when no stream is in flight (or when the cache entry has
  // evicted post-completion via `keepUnusedDataFor: 0`).
  const liveStream = useAgentTurnStream(agent.id)

  // Stop button: best-effort cancel of the in-flight turn. `stopState` tracks
  // the cancel request's own lifecycle (not the stream's) so the button can
  // disable mid-flight and surface a soft failure without touching the
  // stream's `pending`/`error` fields — the turn may still complete normally.
  const [cancelTurn] = nlpApi.useAgentCancelMutation()
  const [stopState, setStopState] = useState<'idle' | 'stopping' | 'failed'>('idle')
  // A new turn resets the stop affordance.
  const lastStopRequestId = useRef<string | null>(null)
  useEffect(() => {
    if (liveStream && lastStopRequestId.current !== liveStream.requestId) {
      lastStopRequestId.current = liveStream.requestId
      setStopState('idle')
    }
  }, [liveStream])

  const handleStop = async () => {
    const agentId = agent.id
    if (!agentId || !liveStream?.turnId) {
      return
    }
    setStopState('stopping')
    try {
      await cancelTurn({ agentId, turnId: liveStream.turnId }).unwrap()
    } catch {
      // Best-effort by design: the turn completes and the reply lands.
      setStopState('failed')
    }
  }

  // Reconciliation watches BOTH delivery paths the persisted reply can arrive
  // through, so a lost push doesn't leave the live item stuck on screen:
  // - WS-pushed entities (`receivedEntities`) — the same inbox `useChat`
  //   merges into the feed (see plugins/chat/src/chat/chat.hook.ts:26), so
  //   the frame that renders the persisted reply is normally the same frame
  //   this flips true — the live item and its replacement swap in one render
  //   pass, no flicker window.
  // - Fetched comment children (`childComments`) — the WS layer never
  //   replays missed pushes on reconnect. If the push for this reply is lost
  //   (laptop sleep, network blip) the comment still reaches the feed via
  //   the incremental children fetch on remount, but `receivedEntities` would
  //   never see it — so we also match against the fetched children, or the
  //   live item would render as a permanent duplicate for the rest of the
  //   session. Same `graphHooks.useEntityChildren` call shape (parentId +
  //   comment filter) as `AgentChatEmptyHeader` so this shares the RTK Query
  //   cache entry instead of introducing a second fetch.
  const receivedEntities = useSelector(state => state.entities.receivedEntities)
  const { entities: childComments } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
    filter: 'comment',
  })
  const liveStreamPersisted =
    liveStream !== null &&
    agent.id !== undefined &&
    hasPersistedReply([...receivedEntities, ...childComments], agent.id, liveStream.requestId)

  // Re-submit a failed turn: fresh requestId/turnId (same rules as
  // handleComment) so RTK Query runs the agent again rather than replaying
  // the cached failure.
  const handleRetry = () => {
    const agentId = agent.id
    if (!agentId || !liveStream?.message) {
      return
    }
    resetCounter()
    startAgentTurn(dispatch, {
      agentId,
      message: liveStream.message,
      trigger: { type: 'chat' },
      requestId: shortUuid(),
      turnId: shortUuid(),
    })
  }

  // Composer hand-off: consume the Redux intent exactly once per nonce — copy
  // into local state, then clear. Redux is the transport; this component owns
  // the lifetime (focus chip until send/dismiss, seed until ChatBox inserts).
  const intent = useComposerIntent(agent.id)
  const [localFocus, setLocalFocus] = useState<AgentFocus | null>(null)
  const [composerSeed, setComposerSeed] = useState<ComposerSeed | null>(null)
  const consumedNonce = useRef(0)

  // Local hand-off state is per-agent. AgentChat can be re-rendered with a
  // different agent without remounting (sidebar tab switch, Content slot), so
  // reset on identity change — otherwise one agent's focus/draft leaks into
  // another's composer.
  const lastAgentId = useRef(agent.id)
  useEffect(() => {
    if (lastAgentId.current === agent.id) {
      return
    }
    lastAgentId.current = agent.id
    setLocalFocus(null)
    setComposerSeed(null)
  }, [agent.id])

  useEffect(() => {
    if (!intent || consumedNonce.current === intent.nonce) {
      return
    }
    consumedNonce.current = intent.nonce
    setLocalFocus(intent.focus ?? null)
    if (intent.draft && intent.draft.length > 0) {
      setComposerSeed({ text: intent.draft, autoSend: intent.autoSend === true, seq: intent.nonce })
    }
    dispatch(features.ui.actions.clearComposerIntent())
  }, [intent, dispatch])

  // Per-thread avatar override: `userId === 'assistant'` comments in this
  // chat are from this agent, so render the agent's resolved icon + color.
  // The factory is stable across renders for the same agent identity — React
  // Compiler memoises the body, so no manual useCallback/useMemo needed.
  const { color, Icon } = resolveAgentVisuals(agent)
  const backgroundColor = getColorCSS(color as ColorName)
  const AgentAvatar: FC = () => (
    <Avatar style={{ backgroundColor, color: 'var(--tuna-content-onColor)' }}>
      <Icon aria-hidden="true" />
    </Avatar>
  )

  const handleComment = (comment: Comment) => {
    if (comment.userId === 'assistant') {
      return
    }
    const text = commentText(comment)
    if (text.length === 0) {
      return
    }
    const agentId = agent.id
    if (!agentId) {
      return
    }
    // New turn — reset the per-turn rate-limit counter on the dispatcher.
    resetCounter()
    // `requestId` is required by `AgentStreamPayload` so RTK Query keys
    // don't collapse across repeated chat submissions (an identical
    // (agentId, message, trigger) tuple would otherwise hit the cached
    // stream instead of running the agent again). Fresh nonce per submit.
    startAgentTurn(dispatch, {
      agentId,
      message: text,
      trigger: { type: 'chat' },
      // requestId is required on AgentStreamPayload — unique per submit so
      // RTK Query cache keys don't collapse across re-asks.
      requestId: shortUuid(),
      // Stable per-turn id for the Phase 3 continuation handshake. Lambda
      // uses this as the turn-state DDB key when the LLM emits a
      // client-side tool call.
      turnId: shortUuid(),
      // Spread `focus` only when present so the payload shape stays clean
      // for plain chat sends (no anchor).
      ...(localFocus ? { focus: localFocus } : {}),
    })
    // One-shot semantics: consume the pending anchor so the next submit is
    // a plain chat send unless the user clicks another block affordance.
    if (localFocus) {
      setLocalFocus(null)
    }
  }

  // Cast: ChatProps.element is Entity (wider). Agent is in the Entity union so
  // the runtime value is fine; the cast tells TS to forward the narrower type
  // through Chat's wider slot.
  const focusLabel = localFocus ? (localFocus.summary ?? localFocus.id) : ''
  return (
    <AssistantAvatarContext.Provider value={AgentAvatar}>
      <div
        className={styles.accentScope}
        style={
          {
            '--agent-accent': backgroundColor,
            '--agent-accent-stroke': 'color-mix(in oklch, var(--agent-accent), var(--tuna-content-primary) 20%)',
            '--agent-accent-bubble-bg': 'color-mix(in oklch, var(--agent-accent) 8%, var(--tuna-palette-action-hover))',
          } as CSSProperties
        }
      >
        {localFocus ? (
          <div
            className={styles.focusChip}
            aria-label={`Focus: ${localFocus.type} · ${focusLabel}`}
            data-testid="focus-chip"
          >
            <span>On {focusLabel}</span>
            <button type="button" aria-label="dismiss focus" onClick={() => setLocalFocus(null)}>
              ×
            </button>
          </div>
        ) : null}
        <Chat {...(props as ChatProps)} composerSeed={composerSeed ?? undefined} onComment={handleComment} />
        {liveStream && !liveStreamPersisted ? (
          <LiveAgentResponse
            stream={liveStream}
            accentColor={backgroundColor}
            onRetry={handleRetry}
            onStop={liveStream.turnId ? handleStop : undefined}
            stopState={stopState}
          />
        ) : null}
      </div>
    </AssistantAvatarContext.Provider>
  )
}
